import MapCompanies from "@/components/company/map-companies";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import {
  MapControls,
  MapControlsItineraryState,
} from "@/components/itinerary/map-controls";
import PageSkeleton from "@/components/page-skeleton";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { setWindowTitle } from "@/lib/window-title";
import { CompanyDTO } from "@shaarp/common";
import { useEffect, useState } from "react";

export default function ListingAssignationsPage() {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [isMapOpened, setIsMapOpened] = useState<boolean>(false);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [companySelected, setCompanySelected] = useState<
    CompanyDTO | undefined
  >(undefined);

  const [itineraryState, setItineraryState] =
    useState<MapControlsItineraryState | null>(null);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const { data } = await api.get(`/listing/assignations/companies`);
      setCompanies(data);
    } catch (error) {
      toastError();
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setWindowTitle(t("listing.page.assignations.title"));
    fetchData();
  }, []);

  return (
    <>
      <div className="absolute top-0 left-0 flex-1 w-full h-[calc(100%)] overflow-auto">
        <ResizablePanelGroup orientation="horizontal" className="w-full">
          <ResizablePanel className="relative">
            <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
              <div className="flex-1 space-y-5">
                {isLoading && <PageSkeleton />}

                {!isLoading && (
                  <>
                    <div className="flex justify-between">
                      <div className="space-y-2">
                        <h2 className="text-2xl font-bold tracking-tight">
                          {t("listing.page.assignations.title")}
                        </h2>
                      </div>

                      <MapControls
                        companies={companies}
                        isMapOpened={isMapOpened}
                        onMapOpenChange={setIsMapOpened}
                        onItineraryStateChange={setItineraryState}
                      />
                    </div>

                    <DataTableCompanies
                      companies={companies}
                      selected={companySelected}
                      onSortedDataChange={itineraryState?.setSortedCompanies}
                      sortingState={[]}
                      overrideColumnsVisibility={["Assignation"]}
                      columnsPersistenceKey="data-table-companies-assignations-v1"
                    />
                  </>
                )}
              </div>
            </div>
          </ResizablePanel>
          {isMapOpened && (
            <>
              <ResizableHandle
                withHandle
                className="transition-all hover:bg-gray-500 active:bg-gray-200"
              />
              <ResizablePanel defaultSize="20%" maxSize="50%">
                <MapCompanies
                  companies={itineraryState?.companiesForMap || companies}
                  selected={companySelected}
                  setSelected={setCompanySelected}
                  shouldCalculateItinerary={
                    itineraryState?.shouldCalculateItinerary || false
                  }
                />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>
    </>
  );
}
